import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { type FieldErrors, FormProvider, type Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useCountriesOptions } from '@/api/countries';
import { useEstateOptions } from '@/api/estate-options';
import { Button } from '@/components/new-ui/button';
import Spinner from '@/components/ui/spinner';
import { getEstateOptions } from '@/features/estates-property/api/estate';
import { getRatingLabel } from '@/features/estates-property/constants/rating-label';
import { FloorOptions } from '@/features/estates-property/utils/floorOptions';

import { useUpdateEstateInfo } from '../api/estate-info';
import type { EstateValidationSchemaType, transformEstate } from '../schemas/estate-validation';
import { getEstateValidationSchema } from '../schemas/estate-validation';
import { RequestInput, RequestSelect } from './request-form-fields';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/analytics'
);

const getFirstErrorMessage = (
  errors: FieldErrors<EstateValidationSchemaType>
): string | undefined => {
  for (const error of Object.values(errors)) {
    if (!error) {
      continue;
    }

    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }

    if (typeof error === 'object') {
      const nestedMessage = getFirstErrorMessage(error as FieldErrors<EstateValidationSchemaType>);

      if (nestedMessage) {
        return nestedMessage;
      }
    }
  }

  return undefined;
};

export const AnalyticsRequestForm: React.FC<{
  estateData: EstateValidationSchemaType | ReturnType<typeof transformEstate>;
  onSaved: (data: EstateValidationSchemaType) => void;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}> = ({ estateData, onSaved, setIsEditing }) => {
  const { t } = useTranslation();
  const estate = route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();

  const { mutateAsync, isPending } = useUpdateEstateInfo({
    mutationConfig: {
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const handleSubmit = async (data: EstateValidationSchemaType) => {
    await mutateAsync({ estate, updatedData: data });
    await queryClient.invalidateQueries({ queryKey: getEstateOptions(estate.id).queryKey });
    await router.invalidate();
    onSaved(data);
    toast.success(t('update-estate-success'));
    setIsEditing(false);
  };

  const handleInvalidSubmit = (errors: FieldErrors<EstateValidationSchemaType>) => {
    toast.error(getFirstErrorMessage(errors) ?? t('fillFormToGenerateAnalytics'));
  };

  const methods = useForm<EstateValidationSchemaType>({
    resolver: zodResolver(getEstateValidationSchema()),
    defaultValues: estateData as EstateValidationSchemaType,
    shouldUseNativeValidation: false,
  });

  const { clearErrors, setValue, watch } = methods;

  const propertyType = watch('estateDetails.propertyType');

  useEffect(() => {
    if (propertyType === 'apartment') {
      setValue('estateAreasRooms.plotArea', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      clearErrors('estateAreasRooms.plotArea');
    } else if (propertyType === 'house') {
      setValue('estateAreasRooms.hasElevator', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      setValue('estateAreasRooms.floor', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      clearErrors(['estateAreasRooms.hasElevator', 'estateAreasRooms.floor']);
    }
  }, [clearErrors, setValue, propertyType]);

  const { data: countries } = useCountriesOptions();
  const { data: estateOptions } = useEstateOptions();
  const countryId = methods.watch('address.countryId');

  const energyClasses = useMemo(() => {
    const classes = estateOptions?.energyClasses ? [...estateOptions.energyClasses] : [];
    const lastIndex = classes.findIndex(({ id }) => id === (countryId === 'AT' ? 'G' : 'H'));
    return lastIndex > -1 ? classes.splice(0, lastIndex + 1) : classes;
  }, [estateOptions, countryId]);

  const ratingOptions = [5, 4, 3, 2, 1].map((rating) => ({
    id: String(rating),
    title: `${rating} (${getRatingLabel(rating)})`,
  }));

  const ratingItems = [
    { key: 'kitchen', title: t('kitchen') },
    { key: 'bathroom', title: t('bathroom') },
    { key: 'flooring', title: t('flooring') },
    { key: 'windows', title: t('windows') },
  ] as const;

  return (
    <div className="new-ui animate-fade-in space-y-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-foreground">{t('analyticsNotAvailable')}</h3>
        <p className="text-muted-foreground">{t('fillFormToGenerateAnalytics')}</p>
      </div>
      <FormProvider {...methods}>
        <form
          className="space-y-6"
          noValidate
          onSubmit={methods.handleSubmit(handleSubmit, handleInvalidSubmit)}
        >
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-6">
            <div className="space-y-4">
              <RequestSelect
                methods={methods}
                name="estateDetails.marketType"
                label={t('marketing-type')}
                options={estateOptions?.marketingType}
                placeholder={t('select')}
              />
              <RequestInput methods={methods} name="address.address" label={t('address')} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <RequestInput methods={methods} name="address.street" label={t('street')} />
                <RequestInput
                  methods={methods}
                  name="address.houseNumber"
                  label={t('houseNumber')}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <RequestInput
                  methods={methods}
                  name="address.lat"
                  label={t('latitude')}
                  type="number"
                  step={0.0001}
                />
                <RequestInput
                  methods={methods}
                  name="address.long"
                  label={t('longitude')}
                  type="number"
                  step={0.0001}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <RequestInput methods={methods} name="address.city" label={t('city')} />
                <RequestInput methods={methods} name="address.postalCode" label={t('postalCode')} />
              </div>
              <RequestSelect
                methods={methods}
                name="address.countryId"
                label={t('country')}
                options={countries}
                placeholder={t('select')}
              />
            </div>
            <div className="space-y-4">
              <RequestSelect
                methods={methods}
                name="estateDetails.propertyType"
                label={t('property-type')}
                options={estateOptions?.propertyType}
                placeholder={t('select')}
              />
              <RequestInput
                methods={methods}
                name="estateDetails.buildingYear"
                label={t('buildingYear')}
                type="number"
                maxLength={4}
              />
              <RequestInput
                methods={methods}
                name="estateAreasRooms.livingArea"
                label={t('livingArea')}
                type="number"
              />
              {propertyType === 'house' ? (
                <RequestInput
                  methods={methods}
                  name={'estateAreasRooms.plotArea' as Path<EstateValidationSchemaType>}
                  label={t('plotArea')}
                  type="number"
                />
              ) : null}
              <RequestInput
                methods={methods}
                name="estateAreasRooms.totalNumberOfRooms"
                label={t('totalNumberOfRooms')}
                type="number"
                decimals={1}
                step={0.1}
              />
              <RequestInput
                methods={methods}
                name="estateAreasRooms.numberOfBathrooms"
                label={t('numberOfBathrooms')}
                type="number"
              />
              <RequestInput
                methods={methods}
                name="estateAreasRooms.balconyArea"
                label={t('balconyArea')}
                type="number"
                decimals={2}
                step={0.01}
              />
              <RequestInput
                methods={methods}
                name="estateAreasRooms.gardenArea"
                label={t('gardenArea')}
                type="number"
                decimals={2}
                step={0.01}
              />
              {propertyType === 'apartment' ? (
                <>
                  <RequestSelect
                    methods={methods}
                    name="estateAreasRooms.floor"
                    label={t('floor')}
                    type="number"
                    options={FloorOptions}
                    placeholder={t('select')}
                  />
                  <RequestSelect
                    methods={methods}
                    name="estateAreasRooms.hasElevator"
                    label={t('hasElevator')}
                    type="number"
                    options={[
                      { id: '1', title: t('yes') },
                      { id: '0', title: t('no') },
                    ]}
                    placeholder={t('select')}
                  />
                </>
              ) : null}
              <RequestSelect
                methods={methods}
                name="estateEnergyDetails.hwbClass"
                label={t('hwbClass')}
                options={energyClasses}
                placeholder={t('select')}
              />
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-medium text-foreground">{t('qualityAndCondition')}</h4>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-6">
              {ratingItems.map(({ key, title }) => (
                <div key={key} className="space-y-4 rounded-lg border border-border p-4">
                  <h5 className="font-medium text-foreground">{title}</h5>
                  <RequestSelect
                    methods={methods}
                    name={`estateRatings.${key}Quality`}
                    label={t('quality')}
                    type="number"
                    options={ratingOptions}
                    placeholder={t('select')}
                  />
                  <RequestSelect
                    methods={methods}
                    name={`estateRatings.${key}Condition`}
                    label={t('condition')}
                    type="number"
                    options={ratingOptions}
                    placeholder={t('select')}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner className="mr-2 !size-5" /> : null}
              {t('save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
